import { trackMiscAction } from 'analytics';
import forge from 'apis/forge';
import { Point, Rect, rectsOverlap } from 'dragonmouse-shared/geometry';
import { Frame, PartialFrame, SceneComment } from 'dragonmouse-shared/interfaces';
import useKey from 'hooks/useKey';
import throttle from 'lodash/throttle';
import { MenuItem, MenuItemAction, MenuItemDivider } from 'menu/MenuItem';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { selectActiveScene, selectFrames } from 'redux/db/reducers';
import {
	canEdit,
	getCommentContaining,
	getFrame,
	getFrameDisplayBounds,
	getFrameContaining,
} from 'redux/db/util';
import {
	addToSelected,
	removeFromSelected,
	setActiveCommentId,
	setHovered,
	setHoveredCommentId,
	setMouseDragMode,
	setSelected,
	showContextMenu,
} from 'redux/mode/actions';
import { Pick } from 'redux/mode/classes';
import { executeCommand } from 'redux/mode/commandManager';
import { MemorializeFrameUpdateCommand } from 'redux/mode/commands';
import { MouseDragMode, selectTransform } from 'redux/mode/reducers';
import store from 'redux/store';
import {
	displayViewOnlyModal,
	getCanvasPoint,
	getCanvasPointForEvent,
	getCanvasRect,
	getScenePoint,
	getSceneRect,
	navigateToComment,
} from 'redux/mode/util';
import { RootReducer } from 'redux/reducers';
import { TargetCanvas } from 'transfer/Target';
import { TransferFrame } from 'transfer/Transfer';
import { TransferBoard, TransferMotion } from 'transfer/TransferBoard';
import * as S from './styles';

// Add RectEdge enum for edge detection
enum RectEdge {
	NONE = 0,
	TOP = 1,
	RIGHT = 2,
	BOTTOM = 4,
	LEFT = 8,
}

type RectEdges = number;

// if the provided canvasClick (a mouse position in canvas coordinates) is close enough to an edge of the frame
// to be considered a click on that edge, return the edge
const clickedEdges = (frame: Frame, canvasClick: Point): RectEdges => {
	// let's get that selected frame's rect in canvasClick coordinates
	const sceneBounds = getFrameDisplayBounds(frame);
	if (sceneBounds) {
		const canvasBounds = getCanvasRect(sceneBounds);
		const EDGE_THRESHOLD = 8; // pixels from edge to consider it an edge click

		let edges = RectEdge.NONE;

		// Check if we're near any edges
		if (Math.abs(canvasClick.x - canvasBounds.x) < EDGE_THRESHOLD) {
			edges |= RectEdge.LEFT;
		}
		if (Math.abs(canvasClick.x - (canvasBounds.x + canvasBounds.width)) < EDGE_THRESHOLD) {
			edges |= RectEdge.RIGHT;
		}
		if (Math.abs(canvasClick.y - canvasBounds.y) < EDGE_THRESHOLD) {
			edges |= RectEdge.TOP;
		}
		if (Math.abs(canvasClick.y - (canvasBounds.y + canvasBounds.height)) < EDGE_THRESHOLD) {
			edges |= RectEdge.BOTTOM;
		}

		return edges;
	}
	return RectEdge.NONE;
};

const getFrameSnapshot = (frame: Frame, canvas: Point): FrameSnapshot => {
	const origin = { x: frame.x, y: frame.y };
	const frameCanvas = getCanvasPoint(origin);
	const delta = { x: canvas.x - frameCanvas.x, y: canvas.y - frameCanvas.y };
	return { frame, origin, delta };
};

// returns a Pick describing the thing on the canvas under the provided canvas coordinates
//
// if selected is non-empty, then the elements of selected that are not the pick will
// become the CanvasPick's selectedFrameSnapshots
export const getCanvasPick = (canvas: Point, selected: Pick[]): CanvasPick => {
	const scene = getScenePoint(canvas);

	// Frame resize is disabled in focus mode.
	const isFocusMode = store.getState().mode.centeredFrameId !== null;

	// First check for edges of selected frames
	if (!isFocusMode) {
		for (const pick of selected) {
			if (!pick.frameId) continue;
			const frame = getFrame(pick.frameId);
			if (!frame) continue;

			const edges = clickedEdges(frame, canvas);
			if (edges !== RectEdge.NONE) {
				const frameBounds = getFrameDisplayBounds(frame);
				if (frameBounds) {
					return new CanvasPickFrameEdge(
						frame.id,
						edges,
						scene,
						frameBounds,
						frame.metadata?.displayScale ?? 1
					);
				}
			}
		}
	}

	// Check comments first (they render on top of frames)
	const transform = selectTransform(store.getState().mode);
	const comment = getCommentContaining(scene, transform.scale);
	if (comment) return new CanvasPickComment(comment, scene);

	const frame = getFrameContaining({ x: scene.x, y: scene.y, width: 0, height: 0 });
	if (frame) {
		const snapshots: FrameSnapshot[] = [];
		selected.forEach((pick) => {
			if (!pick.frameId) return;
			const frame = getFrame(pick.frameId);
			if (!frame) return;
			snapshots.push(getFrameSnapshot(frame, canvas));
		});
		return new CanvasPickFrame(getFrameSnapshot(frame, canvas), snapshots, scene);
	}
	return new CanvasPickCanvas(scene);
};

const isProxyClick = (e: React.MouseEvent<HTMLElement>): boolean => {
	return e.altKey;
};

//------------------------------------------------------------------------------------------------
// CanvasPick class hierarchy
// The canvas has its own collection of things that can be picked, only some of which will be
// passed deeper into the system as an official Pick
//------------------------------------------------------------------------------------------------
export class CanvasPick {
	constructor(
		// the mouse coordinates that initiated the pick
		public readonly scene: Point
	) {}
}
// a picked empty spot on the canvas:
export class CanvasPickCanvas extends CanvasPick {
	constructor(scene: Point) {
		super(scene);
	}
}
// a picked frame:
export class CanvasPickFrame extends CanvasPick {
	constructor(
		// the frame that is picked
		// store its position/offset at the moment of the pick to aid in dragging without drift
		public readonly picked: FrameSnapshot,

		// remember the position/offset of selected frames at the moment of the pick
		// (so they can also be moved without drift when dragging)
		public readonly selectedFrameSnapshots: FrameSnapshot[],
		scene: Point
	) {
		super(scene);
	}
}

// a picked frame edge:
export class CanvasPickFrameEdge extends CanvasPick {
	constructor(
		// the frame ID that contains this edge
		public readonly frameId: string,
		// which edges are being dragged
		public readonly edges: RectEdges,
		// initial scene point where drag started
		scene: Point,
		// initial display bounds (with displayScale applied) when drag started
		public readonly initialRect: Rect,
		// initial displayScale when drag started
		public readonly initialDisplayScale: number
	) {
		super(scene);
	}
}

// a picked comment marker:
export class CanvasPickComment extends CanvasPick {
	constructor(public readonly comment: SceneComment, scene: Point) {
		super(scene);
	}
}

type FrameSnapshot = {
	frame: Frame;
	// the origin of the frame that is picked, in scene coordinates
	origin: Point;
	// the difference between the origin and where the mouse is clicked when making the pick
	// in canvas coordinates
	delta: Point;
};

const SelectTool = () => {
	const dispatch = useDispatch();

	// here we track what the user is hovering in the canvas, this is maintained both when the mouse
	// pointer is up and when it's down
	const [canvasHovered, setCanvasHovered] = useState<CanvasPick | null>(null);

	// the thing we are dragging on the canvas
	const [canvasDragging, setCanvasDragging] = useState<CanvasPick | null>(null);

	// For kinda hacky-reasons, we're storing the last known mouse position in canvas
	// and scene coordinates.
	const [lastCanvas, setLastCanvas] = useState<Point | null>(null);
	const [lastScene, setLastScene] = useState<Point | null>(null);

	// tracks an active drag operation
	const [drag, setDrag] = useState<Drag | null>(null);
	// a drag is pending until the mouse has moved a little bit.
	const [pendingDrag, setPendingDrag] = useState<Drag | null>(null);
	type Drag = {
		// start position of the drag, in canvas coordinates
		clickCanvas: Point;
	};

	const [dragPreview, setDragPreview] = useState<DragPreview>({
		objectRect: null,
	});
	type DragPreview = {
		// the thing being dragged is the drag object, in canvas coordinates
		objectRect: Rect | null;
	};

	// the box that is drawn when dragging out a selection box, in canvas coordinates
	const [selectionBox, setSelectionBox] = useState<Rect | null>(null);

	const { scale, toolModified, selected, frames, activeScene } = useSelector(
		(r: RootReducer) => ({
			scale: selectTransform(r.mode).scale,
			toolModified: r.mode.toolModified,
			selected: r.mode.selected,
			frames: selectFrames(r.db),
			authUser: r.mode.authUser,
			activeScene: selectActiveScene(r.db, r.mode.activeSceneId),
		}),
		shallowEqual
	);

	//------------------------------------------------------------------------------------------------
	// HOOKS
	//------------------------------------------------------------------------------------------------
	const multiSelect = useKey('Shift');

	useEffect(() => {
		if (!lastCanvas || !lastScene) return;

		// When we're dragging a canvas, we need to hop between copy amd move mode
		if (canvasDragging instanceof CanvasPickFrame) {
			// for now, just show the first drag preview
			// (because we've yet to commit to making multiframe option-drag work)
			const previews = updateWhenDraggingFrame(canvasDragging, lastCanvas, lastScene);
			if (previews.length) {
				setDragPreview(previews[0]);
			}
		}
	}, [toolModified]);

	// Now that we are sending updates to the database during the drag, (and there
	// is no clearFrameOverride) we would need to store the starting position of
	// the dragged frame and restore it when the user presses ESC. Doable but I'm
	// going to skip it for now. -dw
	//
	useEffect(
		() => {
			const onKeyDown = (event: KeyboardEvent) => {
				if (canvasDragging && event.key === 'Escape') {
					if (canvasDragging instanceof CanvasPickFrame) {
						canvasDragging.selectedFrameSnapshots.forEach((snapshot) => {
							forge.dbrUpdateFrame({
								update: {
									id: snapshot.frame.id,
									x: snapshot.origin.x,
									y: snapshot.origin.y,
								},
								showToast: true,
							});
						});
					} else if (canvasDragging instanceof CanvasPickFrameEdge) {
						const { frameId, initialDisplayScale, initialRect } = canvasDragging;
						const frame = getFrame(frameId);
						if (frame) {
							forge.dbrUpdateFrame({
								update: {
									id: frameId,
									x: initialRect.x,
									y: initialRect.y,
									metadata: { ...frame.metadata, displayScale: initialDisplayScale },
								},
								showToast: true,
							});
						}
					}
					setCanvasDragging(null);
					setDragPreview({
						objectRect: null,
					});
				}
			};
			window.addEventListener('keydown', onKeyDown);

			return function cleanup() {
				window.removeEventListener('keydown', onKeyDown);
			};
		},
		// re-register the event listener when canvasDragging changes, otherwise it never sees
		// the most recent value of state. Not sure why this happens -- the event listener
		// is apparently a closure over the current state of the component?
		[canvasDragging?.constructor]
	);

	//------------------------------------------------------------------------------------------------
	// UTILITY
	//------------------------------------------------------------------------------------------------

	// right now we're not worrying about whether or not a FrameSnapshot's picked is part of its
	// selectedFrameSnapshots or not. This utility function makes it easy to cleanly get a list wich
	// each snapshot featured exactly once.
	const getAllSnapshots = (handle: CanvasPickFrame): FrameSnapshot[] => {
		const snapshots = handle.selectedFrameSnapshots;
		if (!snapshots.find((s) => s.frame.id === handle.picked.frame.id)) {
			snapshots.push(handle.picked);
		}
		return snapshots;
	};

	// Performs needed updates when the user is dragging a frame
	//
	// Also (kinda grossly) returns an array of DragPreviews to show the user
	// Someimtes the returned array is empty!
	// Since DragPreviews are currently only used when a frame is being duplicated.
	const updateWhenDraggingFrame = (
		handle: CanvasPickFrame,
		canvas: Point,
		scene: Point
	): DragPreview[] => {
		const previews: DragPreview[] = [];
		const snapshots = getAllSnapshots(handle);
		const delta: Point = {
			x: scene.x - handle.scene.x,
			y: scene.y - handle.scene.y,
		};
		if (!canEdit(activeScene)) {
			displayViewOnlyModal();
			return [];
		}

		snapshots.forEach((snapshot) => {
			let changes = {};
			if (toolModified) {
				// move source frame back to its original position/
				// if we're duplicating we don't want it to seem like we're moving the original
				changes = {
					x: snapshot.origin.x,
					y: snapshot.origin.y,
				};
				const frameBounds = getFrameDisplayBounds(snapshot.frame);
				const newFrameRect = {
					x: canvas.x - snapshot.delta.x,
					y: canvas.y - snapshot.delta.y,
					width: frameBounds.width * scale,
					height: frameBounds.height * scale,
				} as Rect;
				previews.push({
					objectRect: newFrameRect,
				});
			} else {
				// compute a new position for the snapshot's frame
				changes = {
					x: Math.round(snapshot.origin.x + delta.x),
					y: Math.round(snapshot.origin.y + delta.y),
				};
			}
			if (Object.keys(changes).length) {
				forge.dbrUpdateFrame({
					update: {
						id: snapshot.frame.id,
						...changes,
					},
				});
			}
		});
		return previews;
	};

	const updateWhenDraggingCanvas = (handle: CanvasPickCanvas, canvas: Point): void => {
		const handleCanvas = getCanvasPoint(handle.scene);
		// draw a box from the handle's scene position to the current canvas position
		const box = {
			x: handleCanvas.x,
			y: handleCanvas.y,
			width: canvas.x - handleCanvas.x,
			height: canvas.y - handleCanvas.y,
		};

		// if width or height are negative, flip x/y accordingly
		if (box.width < 0) {
			box.x += box.width;
			box.width = -box.width;
		}
		if (box.height < 0) {
			box.y += box.height;
			box.height = -box.height;
		}
		setSelectionBox(box);
	};

	const updateCanvasHovered = (canvas: Point, selected: Pick[]) => {
		const pick = getCanvasPick(canvas, selected);
		setCanvasHovered(pick);

		// Update the hovered state in redux
		switch (pick.constructor) {
			case CanvasPickComment:
				dispatch(setHovered(new Pick()));
				dispatch(setHoveredCommentId((pick as CanvasPickComment).comment.id));
				break;
			case CanvasPickFrame:
				const frame = (pick as CanvasPickFrame).picked.frame;
				dispatch(setHovered(new Pick(frame.id)));
				dispatch(setHoveredCommentId(null));
				break;
			case CanvasPickFrameEdge:
				dispatch(setHovered(new Pick((pick as CanvasPickFrameEdge).frameId)));
				dispatch(setHoveredCommentId(null));
				break;
			case CanvasPickCanvas:
				dispatch(setHovered(new Pick()));
				dispatch(setHoveredCommentId(null));
				break;
			default:
				dispatch(setHovered(new Pick()));
				break;
		}
	};

	const initiateDrag = (pick: CanvasPick, canvas: Point) => {
		setCanvasDragging(pick);
		setDrag(null); // seems unlikely we'll have an active drag at this point but doesn't hurt to be safe
		setPendingDrag({
			clickCanvas: canvas,
		});
	};

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------

	const onMouseDown = (e: any) => {
		if (e.button !== 0) return; // Only handle left-clicks
		if (isProxyClick(e)) return;
		const canvas = getCanvasPointForEvent(e);

		dispatch(setMouseDragMode(MouseDragMode.SELECT_TOOL));

		// Clear active comment unless we're clicking on a comment
		if (!(canvasHovered instanceof CanvasPickComment)) {
			dispatch(setActiveCommentId(null));
		}

		// i think we should be calling getCanvasPick to get whatever's unders the mouse
		// and use that as our pick here
		// since hovers don't update when context menu is open
		if (!canvasHovered) return;

		switch (canvasHovered.constructor) {
			case CanvasPickFrame:
				initiateDrag(canvasHovered, canvas);
				const frame = (canvasHovered as CanvasPickFrame).picked.frame;

				// the logic here is intricate
				// it's all based on:
				//   - selected.length -- how many things are selected?
				//   - multiSelect -- is the modifier key pressed?
				//   - alreadySelected -- is the thing we clicked on already selected?
				const alreadySelected = selected.find((f) => f.frameId === frame.id);

				if (selected.length === 0) {
					// just add the thing we clicked on
					// multiSelect doesn't affect this
					dispatch(addToSelected(new Pick(frame.id)));
				} else {
					if (!multiSelect) {
						// when no modifier key is pressed
						// we may be performing a drag.
						// if something is already selected, we're either:
						//    - dragging that new thing (in which case the old selection disappears)
						//    - clicking on the new thing (in which case the new thing replaces the old selection)
						// so in either case we need to remove the old selection
						if (!alreadySelected) {
							dispatch(setSelected([new Pick(frame.id)]));
							const hover = canvasHovered as CanvasPickFrame;
							initiateDrag(new CanvasPickFrame(hover.picked, [], hover.scene), canvas);
						}
					} else {
						// when the modifier key is pressed, we get some fancy behavior
						if (alreadySelected) {
							// if the pick is already selected, we remove it from the selection
							dispatch(removeFromSelected(new Pick(frame.id)));
						} else {
							// if the pick is not already selected, we add it to the selection
							dispatch(addToSelected(new Pick(frame.id)));
						}
					}
				}

				break;
			case CanvasPickFrameEdge:
				const frameEdge = canvasHovered as CanvasPickFrameEdge;
				initiateDrag(canvasHovered, canvas);
				dispatch(setSelected([new Pick(frameEdge.frameId)]));
				break;
			case CanvasPickComment: {
				dispatch(setSelected([]));
				const clickedComment = (canvasHovered as CanvasPickComment).comment;
				navigateToComment(clickedComment);
				break;
			}
			case CanvasPickCanvas:
				// dragging "nothing" means we are dragging from an empty point on the canvas
				initiateDrag(canvasHovered, canvas);
				dispatch(setSelected([]));
				break;
		}
	};

	const onMouseUp = async (e: any) => {
		const canvas = getCanvasPointForEvent(e);

		if (canvasDragging) {
			if (toolModified) {
				if (!(canvasDragging instanceof CanvasPickCanvas)) {
					switch (canvasDragging.constructor) {
						case CanvasPickFrame:
							const snapshots = getAllSnapshots(canvasDragging as CanvasPickFrame);
							snapshots.forEach((snapshot) => {
								const transfer = new TransferFrame(snapshot.frame);
								// offset the scene position by delta before creating the target
								const x = canvas.x - snapshot.delta.x;
								const y = canvas.y - snapshot.delta.y;
								const dropPosition = getScenePoint({ x, y });
								const target = new TargetCanvas(dropPosition);

								const board = new TransferBoard([transfer], TransferMotion.MOVE);

								const complete = target.getComplete(board);
								if (complete) complete();
							});
							break;
					}
				}
			} else {
				switch (canvasDragging.constructor) {
					case CanvasPickFrame:
						const frame = (canvasDragging as CanvasPickFrame).picked.frame;

						if (pendingDrag) {
							// if the drag hasn't started yet, we are actually performing a click
							// on a single frame which always (when no modifier key is pressed)
							// results in just the one frame being selected
							if (!multiSelect) {
								dispatch(setSelected([new Pick(frame.id)]));
							}
						} else {
							const frameSnapshots = (canvasDragging as CanvasPickFrame).selectedFrameSnapshots;
							const before: PartialFrame[] = [];
							const after: PartialFrame[] = [];
							frameSnapshots.forEach((snapshot) => {
								const frame = getFrame(snapshot.frame.id);
								if (frame) {
									before.push({
										id: snapshot.frame.id,
										x: snapshot.origin.x,
										y: snapshot.origin.y,
									});
									after.push({
										id: frame.id,
										x: frame.x,
										y: frame.y,
									});
								} else console.error('error preparing MemorializeFrameUpdateCommand');
							});
							executeCommand(new MemorializeFrameUpdateCommand(before, after));
							// canvasDragging.selectedFrameSnapshots.forEach((snapshot) => {
							// 	forge.dbrUpdateFrame({
							// 		update: {
							// 			id: snapshot.frame.id,
							// 			x: snapshot.origin.x,
							// 			y: snapshot.origin.y,
							// 		},
							// 	});
							// });
						}
						break;
					case CanvasPickFrameEdge: {
						const { initialDisplayScale, initialRect, frameId } =
							canvasDragging as CanvasPickFrameEdge;
						const frame = getFrame(frameId);
						if (frame) {
							const before: PartialFrame = {
								id: frame.id,
								x: initialRect.x,
								y: initialRect.y,
								metadata: { ...frame.metadata, displayScale: initialDisplayScale },
							};
							const after: PartialFrame = {
								id: frame.id,
								x: frame.x,
								y: frame.y,
								metadata: { ...frame.metadata },
							};
							executeCommand(new MemorializeFrameUpdateCommand([before], [after]));
						} else {
							console.error('error preparing MemorializeFrameUpdateCommand');
						}
						break;
					}
					case CanvasPickCanvas:
						// select everything in selection box
						if (!multiSelect) dispatch(setSelected([]));
						// loop through frames and add any whose bounds are within the selection box

						if (selectionBox) {
							// convert selectionBox to scene coordinates
							const sceneBox = getSceneRect(selectionBox);
							frames.forEach((frame) => {
								if (rectsOverlap(sceneBox, getFrameDisplayBounds(frame))) {
									const alreadySelected = selected.find((f) => f.frameId === frame.id);
									if (!alreadySelected) dispatch(addToSelected(new Pick(frame.id)));
								}
							});
						}
						setSelectionBox(null);
						break;
				}
			}
		}

		setCanvasDragging(null);
		setDragPreview({
			objectRect: null,
		});
		setDrag(null);
		setPendingDrag(null);
		setSelectionBox(null);
		dispatch(setMouseDragMode(MouseDragMode.NONE));
	};

	// `useRef` is necessary to keep a record of the throttled function between renders
	// if we remove it, the throttled function is re-created everytime and hence does nothing
	// but this also requires us to pass more arguments since the function doesn't get re-created
	// when the outside constants change, so they could be stale if we didn't pass them in
	const onMouseMove = useRef(
		throttle(
			async (
				e: any,
				// we have to pass various state-dependent things into the throttled function
				// because it doesn't get re-created when the outside values change
				canvasDragging: any,
				canvasHovered: CanvasPick | null,
				drag: Drag | null,
				pendingDrag: Drag | null,
				updateWhenDraggingFrame: (
					handle: CanvasPickFrame,
					canvas: Point,
					scene: Point
				) => DragPreview[],
				updateWhenDraggingCanvas: (handle: CanvasPickCanvas, canvas: Point) => void,
				selected: Pick[],
				activeScene: any
			) => {
				if (e as MouseEvent) {
					const canvas = getCanvasPointForEvent(e);
					const scene = getScenePoint(canvas);

					updateCanvasHovered(canvas, selected);

					// update (or clear) our drag preview based on what we're dragging (or not dragging)
					let newDragPreviews: DragPreview[] = [];

					if (canvasDragging) {
						let active = false;
						if (drag) active = true;

						// activate the drag if needed
						if (pendingDrag) {
							if (!pendingDrag.clickCanvas)
								console.error(
									"Pending drag's clickCanvas should always be set when the user has clicked on something that's draggable and is moving their mouse"
								);
							else {
								const dx = canvas.x - pendingDrag.clickCanvas.x;
								const dy = canvas.y - pendingDrag.clickCanvas.y;
								const dist = Math.sqrt(dx * dx + dy * dy);
								if (dist > 5) {
									active = true;
									setDrag(pendingDrag);
									setPendingDrag(null);
								}
							}
						}

						if (active) {
							switch (canvasDragging.constructor) {
								case CanvasPickFrameEdge: {
									if (!canEdit(activeScene)) {
										displayViewOnlyModal();
										return;
									}

									const { edges, initialRect, initialDisplayScale, frameId } =
										canvasDragging as CanvasPickFrameEdge;
									const deltaX = scene.x - canvasDragging.scene.x;
									const deltaY = scene.y - canvasDragging.scene.y;
									const MIN_SCALE = 0.1;

									// compute scale ratio from whichever edge(s) are being dragged
									let scaleRatio = 1;
									if (edges & RectEdge.RIGHT) {
										scaleRatio = (initialRect.width + deltaX) / initialRect.width;
									} else if (edges & RectEdge.LEFT) {
										scaleRatio = (initialRect.width - deltaX) / initialRect.width;
									} else if (edges & RectEdge.BOTTOM) {
										scaleRatio = (initialRect.height + deltaY) / initialRect.height;
									} else if (edges & RectEdge.TOP) {
										scaleRatio = (initialRect.height - deltaY) / initialRect.height;
									}

									// for corners, average both axes
									if (
										edges & (RectEdge.LEFT | RectEdge.RIGHT) &&
										edges & (RectEdge.TOP | RectEdge.BOTTOM)
									) {
										const hRatio =
											edges & RectEdge.RIGHT
												? (initialRect.width + deltaX) / initialRect.width
												: (initialRect.width - deltaX) / initialRect.width;
										const vRatio =
											edges & RectEdge.BOTTOM
												? (initialRect.height + deltaY) / initialRect.height
												: (initialRect.height - deltaY) / initialRect.height;
										scaleRatio = (hRatio + vRatio) / 2;
									}

									let newScale = Math.max(MIN_SCALE, initialDisplayScale * scaleRatio);

									// snap to 0.5, 1.0, 2.0
									const SNAP_THRESHOLD = 0.05;
									for (const snap of [0.5, 1.0, 2.0]) {
										if (Math.abs(newScale - snap) < SNAP_THRESHOLD) {
											newScale = snap;
											break;
										}
									}

									const currentFrame = getFrame(frameId);
									if (currentFrame) {
										// adjust origin so the opposite corner stays pinned
										const newWidth = currentFrame.width * newScale;
										const newHeight = currentFrame.height * newScale;
										const update: any = {
											id: frameId,
											metadata: { ...currentFrame.metadata, displayScale: newScale },
										};
										if (edges & RectEdge.LEFT) {
											update.x = Math.round(initialRect.x + initialRect.width - newWidth);
										}
										if (edges & RectEdge.TOP) {
											update.y = Math.round(initialRect.y + initialRect.height - newHeight);
										}
										forge.dbrUpdateFrame({ update, showToast: true });
									}
									break;
								}
								case CanvasPickFrame:
									newDragPreviews = updateWhenDraggingFrame(
										canvasDragging as CanvasPickFrame,
										canvas,
										scene
									);
									break;
								case CanvasPickCanvas:
									updateWhenDraggingCanvas(canvasDragging as CanvasPickCanvas, canvas);
									break;
							}
						}
					} else {
						if (canvasHovered) {
							switch (canvasHovered.constructor) {
								case CanvasPickFrame:
									const frame = (canvasHovered as CanvasPickFrame).picked.frame;
									dispatch(setHovered(new Pick(frame.id)));
									break;
								default:
									dispatch(setHovered(null));
							}
						}
					}
					if (newDragPreviews.length) {
						setDragPreview(newDragPreviews[0]);
					}
					setLastCanvas(canvas);
					setLastScene(scene);
				}
			},
			20
		)
	);

	const onDoubleClick = async (e: React.MouseEvent) => {
		dispatch(setMouseDragMode(MouseDragMode.SELECT_TOOL));

		const canvas = getCanvasPointForEvent(e);
		const pick = getCanvasPick(canvas, []);
		if (!pick) return;

		switch (pick.constructor) {
			case CanvasPickFrame:
				const frame = (pick as CanvasPickFrame).picked.frame;
				trackMiscAction('double_click_frame_canvas');
				MenuItemAction.createCenterFrame(frame).callback();
				break;
			default:
				break;
		}
	};

	const onContextMenu = async (e: React.MouseEvent) => {
		if (e.target instanceof HTMLElement) {
			// We have to call preventDefault before we do anything async (such as the getCanvasPick call
			// that is currently down below). Otherwise, the default context menu can appear before we
			// have a chance to prevent it.
			// e.preventDefault();
			if (!canvasHovered) return;

			// canvasHovered can't be trusted here. why?
			// when the context menu is showing, onMouseMove doesn't fire
			// since onMouseMove is responsible for updating canvasHovered, it doesn't update
			// i'm not sure why we don't get onMouseMoves while the context menu is showing but, for
			// now, it's clean and robust to do a pick to determine what we're clicking on
			const scenePoint = getScenePoint(getCanvasPointForEvent(e));
			const canvasPoint = getCanvasPointForEvent(e);
			const pick = getCanvasPick(canvasPoint, selected);

			let menuItems: MenuItem[] = [];

			switch (pick.constructor) {
				case CanvasPickFrame:
					const frame = (pick as CanvasPickFrame).picked.frame;

					// if the picked frame is part of a multi-frame selection, set them all as the target
					// otherwise set just the pick as the target
					const targetFrameIds: string[] = (
						selected.length && selected.find((f) => f.frameId === frame.id)
							? selected.map((f) => f.frameId).filter((id): id is string => id !== undefined)
							: [frame.id]
					).filter((id) => id !== undefined);

					const sourceChatId = (pick as CanvasPickFrame).picked.frame.metadata.sourceChatId;

					const selectedFrames = targetFrameIds
						.map((id) => getFrame(id))
						.filter((f): f is Frame => f !== null);

					const isPreviewable = selectedFrames.length === 1;

					menuItems.push(
						MenuItemAction.createCopyFrameUrl(
							selectedFrames.length === 1 ? selectedFrames[0] : selectedFrames
						),
						MenuItemAction.createCopyFrameId(frame.id),
						MenuItemAction.createCopyFrameImage(frame),
						MenuItemAction.createCopyFrameSvg(frame),
						MenuItemAction.createCopyFrameHtml(frame),
						MenuItemAction.createDownloadFrame(frame, 'html'),
						new MenuItemDivider(),
						MenuItemAction.createCenterFrame(frame),
						new MenuItemDivider(),
						...(isPreviewable
							? [
									MenuItemAction.createOpenPrototype(frame.id),
									MenuItemAction.createOpenPrototypeInNewTab(frame),
									new MenuItemDivider(),
							  ]
							: []),
						MenuItemAction.createDeleteFrames(targetFrameIds),
						MenuItemAction.createRetry((pick as CanvasPickFrame).picked.frame),
						new MenuItemDivider(),
						...(sourceChatId
							? [MenuItemAction.createScrollToFrameInChat(sourceChatId, frame.id)]
							: [])
					);
					break;
				case CanvasPickCanvas:
					const paste = await MenuItemAction.createPaste(new TargetCanvas(scenePoint));
					if (paste) menuItems.push(paste);
					break;
			}

			if (menuItems.length) {
				dispatch(
					showContextMenu({
						point: {
							// Show the menu slightly to the right of the mouse cursor so the cursor doesn't get in
							// the way of reading the menu and the first item is not immediately hovered.
							x: e.clientX + 5,
							y: e.clientY,
						},
						menuItems,
					})
				);
			}
		}
	};

	const cursor = useMemo((): string => {
		if (canvasHovered instanceof CanvasPickFrameEdge) {
			const { edges } = canvasHovered;
			let direction = '';
			if (edges & RectEdge.TOP) direction += 'n';
			if (edges & RectEdge.BOTTOM) direction += 's';
			if (edges & RectEdge.RIGHT) direction += 'e';
			if (edges & RectEdge.LEFT) direction += 'w';

			switch (direction) {
				case 'n':
				case 's':
					return 'ns-resize';
				case 'e':
				case 'w':
					return 'ew-resize';
				case 'ne':
				case 'sw':
					return 'nesw-resize';
				case 'nw':
				case 'se':
					return 'nwse-resize';
				default:
					return 'auto';
			}
		}
		return 'auto';
	}, [canvasHovered, toolModified]);

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------
	return (
		<S.Base
			onMouseUp={onMouseUp}
			onMouseLeave={onMouseUp}
			onMouseDown={onMouseDown}
			onDoubleClick={onDoubleClick}
			onMouseMove={(e) =>
				onMouseMove.current(
					e,
					canvasDragging,
					canvasHovered,
					drag,
					pendingDrag,
					updateWhenDraggingFrame,
					updateWhenDraggingCanvas,
					selected,
					activeScene
				)
			}
			onContextMenu={onContextMenu}
			cursor={cursor}
		>
			{dragPreview.objectRect && <S.DragObject canvasRect={dragPreview.objectRect}></S.DragObject>}
			{selectionBox && <S.SelectionBox canvasRect={selectionBox}></S.SelectionBox>}
		</S.Base>
	);
	//------------------------------------------------------------------------------------------------
};

export default SelectTool;
